/* NFC Solutions — Local Business Database (IndexedDB)
 * -----------------------------------------------------------------
 * Every installed device keeps a complete local mirror of the
 * business it belongs to. Reads that will one day serve from local
 * storage first pass through this module; writes go straight to the
 * server today, and on success the affected records are refreshed
 * (via the realtime WS bus).
 *
 * Business isolation: only ONE business's data ever lives on-device
 * at a time. If the caller detects a different `business.slug`, we
 * wipe the store before hydrating with the new snapshot.
 */

const DB_NAME = "nfcs_local_db";
const DB_VERSION = 3; // bumped in Session 25.5 for comprehensive business snapshot

// Every collection we mirror. Ordered by dependency: business first,
// then reference data (categories, tables, users, modifiers), then
// transactional (orders, sessions). Session 25.5 expanded this to
// cover every business-scoped collection the backend can return so
// each installed device becomes a genuine complete working copy of
// its assigned business.
//
// Store-name mapping:
//   • `meta`                — key/value diagnostics + install flags (key: "key")
//   • `business`            — singleton (key: "self")
//   • `offline_credentials` — one row per enrolled user (key: "user_id")
//   • `event_log`           — bounded ring buffer of recent events (key: "id")
//   • `event_queue`         — offline mutation queue for Session 26 (key: "id")
//   • everything else       — mirrors the server collection (key: "id")
export const STORE_NAMES = [
  "meta",
  "business",
  "categories",
  "menu_items",
  "modifiers",
  "modifier_groups",
  "sides_categories",
  "sides",
  "tables",
  "tags",
  "users",
  "role_permissions",
  "vouchers",
  "open_accounts",
  "table_sessions",
  "orders",
  "specials",
  "offline_credentials",
  // ── Session 25.5 additions — comprehensive business config ─────
  "printers",
  "printer_configs",
  "discounts",
  "discount_configs",
  "promotions",
  "payment_methods",
  "payment_configs",
  "qr_configs",
  "nfc_configs",
  "device_configs",
  "dashboard_settings",
  "kitchen_settings",
  "bar_settings",
  "customer_display_settings",
  "seating_layouts",
  "floor_plans",
  "inventory_items",
  "inventory_batches",
  "inventory_categories",
  "suppliers",
  "analytics_cache",
  "reports_cache",
  "audit_logs",
  // ── Local Event Engine (Session 25.5 skeleton for Session 26) ──
  "event_log",
  "event_queue",
];

let _dbPromise = null;

/** Open (creating on first run) the IndexedDB instance. */
export function openLocalDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not supported in this environment"));
      return;
    }
    // Session 25.7 — Android crash resilience: an incomplete/corrupt
    // upgrade from an older DB version was suspected in the "NFC keeps
    // stopping" reports. If the initial open fails hard, we blow the
    // database away and retry once with a fresh schema.
    const _openOnce = (allowRecovery) => {
      let req;
      try { req = indexedDB.open(DB_NAME, DB_VERSION); }
      catch (e) { reject(e); return; }
      req.onupgradeneeded = () => {
        try {
          const db = req.result;
          STORE_NAMES.forEach((name) => {
            if (!db.objectStoreNames.contains(name)) {
              const keyPath =
                name === "meta" ? "key"
                : name === "offline_credentials" ? "user_id"
                : "id";
              try { db.createObjectStore(name, { keyPath }); } catch (_) { /* ignore per-store */ }
            }
          });
        } catch (_) { /* upgrade errors bubble via req.onerror */ }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        const err = req.error || new Error("Failed to open IndexedDB");
        if (allowRecovery) {
          // Delete + retry ONCE — the fresh open will hit onupgradeneeded
          // and create every store from scratch.
          try {
            const del = indexedDB.deleteDatabase(DB_NAME);
            del.onsuccess = () => _openOnce(false);
            del.onerror = () => reject(err);
            del.onblocked = () => reject(err);
          } catch (_) { reject(err); }
        } else {
          reject(err);
        }
      };
      req.onblocked = () => reject(new Error("IndexedDB is blocked (close other tabs)"));
    };
    _openOnce(true);
  });
  return _dbPromise;
}

function _tx(db, storeNames, mode = "readonly") {
  return db.transaction(storeNames, mode);
}

function _promisify(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Wipe every store — used when switching businesses on the device. */
export async function wipeLocalDb() {
  const db = await openLocalDb();
  const tx = _tx(db, STORE_NAMES, "readwrite");
  STORE_NAMES.forEach((name) => tx.objectStore(name).clear());
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
  });
}

/** Read the entire contents of a collection. */
export async function readAll(store) {
  const db = await openLocalDb();
  const tx = _tx(db, [store], "readonly");
  return _promisify(tx.objectStore(store).getAll());
}

/** Read one record by id. */
export async function readOne(store, id) {
  const db = await openLocalDb();
  const tx = _tx(db, [store], "readonly");
  return _promisify(tx.objectStore(store).get(id));
}

/** Overwrite a whole collection atomically. */
export async function replaceCollection(store, rows) {
  const db = await openLocalDb();
  const tx = _tx(db, [store], "readwrite");
  const os = tx.objectStore(store);
  os.clear();
  (rows || []).forEach((r) => {
    if (!r || (store !== "meta" && !r.id)) return;
    try { os.put(r); } catch (_) { /* skip malformed */ }
  });
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
  });
}

/** Upsert a single record. */
export async function putOne(store, row) {
  const db = await openLocalDb();
  const tx = _tx(db, [store], "readwrite");
  tx.objectStore(store).put(row);
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
  });
}

/** Delete a record by id. */
export async function deleteOne(store, id) {
  const db = await openLocalDb();
  const tx = _tx(db, [store], "readwrite");
  tx.objectStore(store).delete(id);
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
  });
}

async function readMeta(key) {
  try {
    const row = await readOne("meta", key);
    return row ? row.value : null;
  } catch (_) { return null; }
}

async function writeMeta(key, value) {
  try { await putOne("meta", { key, value }); } catch (_) { /* noop */ }
}

/**
 * Hydrate the local DB from a `/api/sync/snapshot` payload.
 * Automatically wipes local data if the payload belongs to a
 * different business than what's stored locally.
 */
export async function hydrateFromSnapshot(snapshot, { onProgress } = {}) {
  if (!snapshot || typeof snapshot !== "object") return;
  const business = snapshot.business || null;
  const incomingSlug = ((business && business.slug) || "").toString().toLowerCase();
  const previousSlug = ((await readMeta("business_slug")) || "").toString().toLowerCase();
  if (incomingSlug && previousSlug && incomingSlug !== previousSlug) {
    // Different business — enforce single-business-per-device.
    await wipeLocalDb();
  }
  // Business singleton — normalise to have `id: "self"` for the
  // keyed store.
  if (business) {
    const record = { ...business, id: "self" };
    await replaceCollection("business", [record]);
  }
  const collections = snapshot.collections || {};
  // Session 25.5: write collections SEQUENTIALLY (rather than
  // Promise.all) so the install progress screen can report meaningful
  // per-collection progress. Sequential writes on IndexedDB are still
  // very fast (single-digit ms per collection) but give us a real
  // progress signal.
  const keys = Object.keys(collections);
  let done = 0;
  const total = keys.length;
  for (const name of keys) {
    if (STORE_NAMES.includes(name)) {
      const rows = collections[name] || [];
      try { await replaceCollection(name, rows); }
      catch (e) {
        // Never fail the whole hydration for one bad collection —
        // log and move on so the app has as much data as possible.
        console.warn(`[localDb] failed to hydrate "${name}":`, e?.message || e);
      }
      done += 1;
      if (typeof onProgress === "function") {
        try { onProgress({ done, total, name, count: rows.length }); }
        catch (_) { /* noop */ }
      }
    } else {
      // Unknown collection — silently skip. Forward-compat: newer
      // backends may include collections we don't know about yet.
      done += 1;
      if (typeof onProgress === "function") {
        try { onProgress({ done, total, name, count: 0, skipped: true }); }
        catch (_) { /* noop */ }
      }
    }
  }
  await writeMeta("business_slug", incomingSlug);
  await writeMeta("last_snapshot_at", snapshot.server_time || new Date().toISOString());
  await writeMeta("sync_cursor", snapshot.cursor || snapshot.server_time || new Date().toISOString());
  if (snapshot.snapshot_version != null) {
    await writeMeta("snapshot_version", snapshot.snapshot_version);
  }
  if (snapshot.snapshot_id) {
    await writeMeta("snapshot_id", snapshot.snapshot_id);
  }
}

/** Meta info for Settings / Device page. */
export async function getLocalDbInfo() {
  try {
    const [slug, lastAt, cursor, installAt, snapVersion, snapId, imageCount, imageStrategy] = await Promise.all([
      readMeta("business_slug"),
      readMeta("last_snapshot_at"),
      readMeta("sync_cursor"),
      readMeta("install_completed_at"),
      readMeta("snapshot_version"),
      readMeta("snapshot_id"),
      readMeta("install_image_count"),
      readMeta("image_cache_strategy"),
    ]);
    const counts = {};
    const db = await openLocalDb();
    await Promise.all(STORE_NAMES.filter((n) => n !== "meta").map(async (n) => {
      try {
        const tx = _tx(db, [n], "readonly");
        counts[n] = await _promisify(tx.objectStore(n).count());
      } catch (_) { counts[n] = 0; }
    }));
    return {
      slug,
      lastSnapshotAt: lastAt,
      cursor,
      counts,
      installCompletedAt: installAt || null,
      snapshotVersion: snapVersion ?? null,
      snapshotId: snapId || null,
      imageCount: Number(imageCount) || 0,
      imageStrategy: imageStrategy || null,
    };
  } catch (_) {
    return null;
  }
}

/**
 * Fast "do we have any local data yet?" check. Used by dashboard
 * mount logic to decide whether to render from local first (yes) or
 * fall back to a skeleton loader (no). Cheap — just reads a meta row.
 */
export async function hasLocalSnapshot() {
  try {
    const at = await readMeta("last_snapshot_at");
    return Boolean(at);
  } catch (_) { return false; }
}

/**
 * Return the singleton business record kept in the `business` store.
 * Normalises the synthetic `id: "self"` marker off so callers see
 * the real business shape.
 */
export async function getBusiness() {
  try {
    const rec = await readOne("business", "self");
    if (!rec) return null;
    const { id: _self, ...rest } = rec;
    return rest;
  } catch (_) { return null; }
}

/**
 * Upsert many records into a store WITHOUT clearing it first. This is
 * the primitive future delta-sync will lean on: WS events give us a
 * changed record, we `bulkPut([...])` it, and dashboards read from
 * IndexedDB on their next render. Skips malformed entries silently.
 */
export async function bulkPut(store, rows) {
  if (!rows || rows.length === 0) return;
  const db = await openLocalDb();
  const tx = _tx(db, [store], "readwrite");
  const os = tx.objectStore(store);
  rows.forEach((r) => {
    if (!r || (store !== "meta" && !r.id)) return;
    try { os.put(r); } catch (_) { /* skip malformed */ }
  });
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
  });
}

/**
 * Read a bundle of collections needed to render a dashboard from
 * IndexedDB. Returned in the same shape the server's /sync/snapshot
 * endpoint uses (business + collections dict), so callers can treat
 * local + remote payloads uniformly.
 *
 * Returns `null` if no local snapshot has been hydrated yet.
 */
export async function loadDashboardBundle() {
  try {
    const primed = await hasLocalSnapshot();
    if (!primed) return null;
    const business = await getBusiness();
    const wanted = STORE_NAMES.filter((n) => n !== "meta" && n !== "business");
    const collections = {};
    await Promise.all(wanted.map(async (name) => {
      try { collections[name] = await readAll(name); }
      catch (_) { collections[name] = []; }
    }));
    return { business, collections, source: "local" };
  } catch (_) { return null; }
}

/**
 * Event-driven patcher. Given a realtime WS message
 * `{ type, payload, ts }`, upsert / delete the affected records in
 * IndexedDB so the local store stays in step with the server WITHOUT
 * a full snapshot re-download.
 *
 * Best-effort — unknown event types are ignored. Never throws.
 */
export async function applyRealtimeEvent(msg) {
  if (!msg || typeof msg !== "object") return;
  const type = String(msg.type || "");
  const payload = msg.payload || {};
  try {
    switch (type) {
      case "order_created":
      case "order_held_for_confirmation":
      case "order_updated":
      case "order_confirmed_by_staff":
      case "order_rejected_by_staff":
      case "order_status_changed":
      case "payment_recorded":
      case "payment_edited":
      case "split_payment_processed":
      case "split_payment_edited":
      case "bill_split":
      case "bill_split_cleared":
      case "voucher_applied":
      case "voucher_removed": {
        const order = payload.order || payload;
        if (order && order.id) await putOne("orders", order);
        const ts = payload.table_session || payload.session;
        if (ts && ts.id) await putOne("table_sessions", ts);
        break;
      }
      case "table_session_opened":
      case "table_session_moved":
      case "table_session_updated": {
        const ts = payload.table_session || payload;
        if (ts && ts.id) await putOne("table_sessions", ts);
        break;
      }
      case "table_session_closed": {
        const ts = payload.table_session || payload;
        if (ts && ts.id) await putOne("table_sessions", ts);
        break;
      }
      case "waiter_called":
      case "call_acknowledged": {
        // Waiter calls aren't currently mirrored to a local collection;
        // no-op until a `waiter_calls` store is added. Leaving this
        // branch present so the future engine has a hook to fill in.
        break;
      }
      case "menu_item_updated":
      case "menu_item_created":
      case "product_updated": {
        const item = payload.menu_item || payload.item || payload;
        if (item && item.id) await putOne("menu_items", item);
        break;
      }
      case "menu_item_deleted": {
        const id = payload.id || payload.menu_item_id;
        if (id) await deleteOne("menu_items", id);
        break;
      }
      case "category_updated":
      case "category_created": {
        const cat = payload.category || payload;
        if (cat && cat.id) await putOne("categories", cat);
        break;
      }
      case "category_deleted": {
        const id = payload.id || payload.category_id;
        if (id) await deleteOne("categories", id);
        break;
      }
      case "voucher_updated": {
        const v = payload.voucher || payload;
        if (v && v.id) await putOne("vouchers", v);
        break;
      }
      default:
        // Ignore unknown types — dashboards can still re-fetch on demand.
        break;
    }
    // Advance the sync cursor so a future delta call knows how far we've
    // caught up locally.
    try {
      await writeMeta("sync_cursor", msg.ts || new Date().toISOString());
    } catch (_) { /* noop */ }
  } catch (e) { /* silent — event application is best-effort */
    console.warn("[localDb] applyRealtimeEvent error:", e?.message || e);
  }
}


// ═══════════════════════════════════════════════════════════════════════════
// Offline Authentication (Session 25 — True Local Application, Milestone A)
// ═══════════════════════════════════════════════════════════════════════════
// The device stores a per-user PBKDF2 verifier package issued by the server
// at online-login time. The server bcrypt hash never leaves the server; only
// a per-issuance PBKDF2 (SHA-256, 200k iterations) verifier does. Offline
// PIN entry is validated by running the same PBKDF2 locally and comparing
// against the stored verifier in constant time.
//
// Package shape (matches /api/auth/offline/enroll response):
//   {
//     user_id, role,
//     offline_token, issued_at,
//     verifier_salt, verifier_hash, iterations,
//   }

const OFFLINE_CRED_STORE = "offline_credentials";

/** Store (or replace) the offline credential package for a user. */
export async function storeOfflineCredentials(userId, pkg) {
  if (!userId || !pkg) return;
  const db = await openLocalDb();
  const tx = _tx(db, [OFFLINE_CRED_STORE], "readwrite");
  const record = {
    user_id: String(userId),
    role: pkg.role || null,
    offline_token: pkg.offline_token,
    verifier_salt: pkg.verifier_salt,
    verifier_hash: pkg.verifier_hash,
    iterations: Number(pkg.iterations) || 200_000,
    issued_at: pkg.issued_at || new Date().toISOString(),
    last_refresh_at: pkg.last_refresh_at || pkg.issued_at || new Date().toISOString(),
  };
  tx.objectStore(OFFLINE_CRED_STORE).put(record);
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
  });
}

/** Return the stored offline credential package for a user (or null). */
export async function getOfflineCredentials(userId) {
  if (!userId) return null;
  try {
    return (await readOne(OFFLINE_CRED_STORE, String(userId))) || null;
  } catch (_) { return null; }
}

/** Delete a user's offline credential package (revocation / signout). */
export async function clearOfflineCredentials(userId) {
  if (!userId) return;
  try { await deleteOne(OFFLINE_CRED_STORE, String(userId)); } catch (_) { /* noop */ }
}

/** Return the raw list of every user_id with offline credentials. */
export async function listOfflineCredentialUserIds() {
  try {
    const rows = await readAll(OFFLINE_CRED_STORE);
    return rows.map((r) => r.user_id).filter(Boolean);
  } catch (_) { return []; }
}

/** Does any user with the given role currently have offline creds on this
 * device? Used by the login screen to decide whether to offer the offline
 * sign-in path. */
export async function hasOfflineCredentialsForRole(role) {
  if (!role) return false;
  try {
    const rows = await readAll(OFFLINE_CRED_STORE);
    return rows.some((r) => r && r.role === role);
  } catch (_) { return false; }
}

// Hex helpers — Web Crypto returns ArrayBuffers; the server stores hex.
function _hexToBytes(hex) {
  if (typeof hex !== "string") return new Uint8Array();
  const clean = hex.trim();
  if (!/^[0-9a-fA-F]*$/.test(clean) || clean.length % 2) return new Uint8Array();
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(clean.substr(i * 2, 2), 16);
  }
  return out;
}
function _bytesToHex(bytes) {
  const arr = new Uint8Array(bytes);
  let out = "";
  for (let i = 0; i < arr.length; i += 1) {
    const h = arr[i].toString(16);
    out += h.length === 1 ? "0" + h : h;
  }
  return out;
}
function _constantTimeEqualHex(a, b) {
  if (typeof a !== "string" || typeof b !== "string") return false;
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

/**
 * Derive a PBKDF2 hash of `pin` matching the server-issued package
 * (`salt`, `iterations`). Returns a hex string.
 *
 * Uses `window.crypto.subtle` — available on every modern browser
 * (including installed PWAs on Android WebView 76+ and iOS Safari 11+).
 */
export async function derivePinVerifier(pin, saltHex, iterations) {
  const subtle = (typeof window !== "undefined" && window.crypto && window.crypto.subtle) || null;
  if (!subtle) throw new Error("Web Crypto SubtleCrypto is not available in this browser");
  const enc = new TextEncoder();
  const key = await subtle.importKey(
    "raw",
    enc.encode(String(pin)),
    { name: "PBKDF2" },
    false,
    ["deriveBits"],
  );
  const bits = await subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: _hexToBytes(saltHex),
      iterations: Number(iterations) || 200_000,
      hash: "SHA-256",
    },
    key,
    256, // 32 bytes — must match server (hashlib.pbkdf2_hmac "sha256")
  );
  return _bytesToHex(bits);
}

/**
 * Verify a PIN entered offline against the stored PBKDF2 verifier for
 * this user. Returns the credential package on success (so the caller
 * can mint an offline session that references the offline_token), or
 * null on failure. Constant-time — never leaks timing.
 */
export async function verifyPinOffline(userId, pin) {
  const pkg = await getOfflineCredentials(userId);
  if (!pkg || !pkg.verifier_salt || !pkg.verifier_hash) return null;
  try {
    const derived = await derivePinVerifier(pin, pkg.verifier_salt, pkg.iterations || 200_000);
    if (_constantTimeEqualHex(derived, pkg.verifier_hash)) return pkg;
    return null;
  } catch (_) { return null; }
}

// ═══════════════════════════════════════════════════════════════════════════
// Install metadata + Offline Readiness Verification
// ═══════════════════════════════════════════════════════════════════════════
// These helpers make Settings' Local Status card, and the install progress
// screen, single sources of truth. Everything reads from IndexedDB meta.

/** Mark that the initial full sync has completed successfully. Written
 * ONLY after `verifyOfflineReadiness()` passes.
 *
 * Session 27.62 — Write the localStorage backup flag SYNCHRONOUSLY
 * upfront BEFORE attempting the IndexedDB writes. On some Android
 * WebViews (Capacitor APK) the IDB `meta` store silently fails to
 * persist across app relaunches even though the write appears to
 * succeed in-session. That was causing the install wizard to re-run
 * on every login. localStorage is far more reliable on those WebViews
 * and now acts as the primary source of truth for "is this device
 * already installed?" — with IDB as the secondary (richer metadata)
 * store. */
export async function markInstallCompleted(details = {}) {
  const ts = new Date().toISOString();
  // Session 27.62 — localStorage FIRST. If this succeeds we've already
  // won the "install remembered" battle; IDB failures below are
  // cosmetic (they only affect the Settings > Local Status card).
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("nfcs:install:completed_at_backup", ts);
      if (details.snapshot_id) {
        localStorage.setItem("nfcs:install:snapshot_id", String(details.snapshot_id));
      }
      if (details.snapshot_version != null) {
        localStorage.setItem("nfcs:install:snapshot_version", String(details.snapshot_version));
      }
    }
  } catch (_) { /* localStorage may be unavailable in private mode */ }
  try {
    await writeMeta("install_completed_at", ts);
    if (details.snapshot_version != null) {
      await writeMeta("install_snapshot_version", details.snapshot_version);
    }
    if (details.snapshot_id) {
      await writeMeta("install_snapshot_id", details.snapshot_id);
    }
    if (details.image_count != null) {
      await writeMeta("install_image_count", details.image_count);
    }
  } catch (_) { /* noop — localStorage backup already stored above */ }
  return ts;
}

/** Read the install completion timestamp (null if never completed).
 *  Session 27.62 — localStorage is now checked FIRST because IDB
 *  writes to the `meta` store were silently dropping across app
 *  relaunches on some Capacitor APK WebViews, causing the install
 *  wizard to re-run on every login. localStorage survives WebView
 *  process restarts reliably. IDB remains a fallback so pre-27.62
 *  installs (which only wrote to IDB) are still recognised. */
export async function getInstallCompletedAt() {
  try {
    if (typeof localStorage !== "undefined") {
      const backup = localStorage.getItem("nfcs:install:completed_at_backup");
      if (backup) return backup;
    }
  } catch (_) { /* fall through to IDB */ }
  try {
    const idb = await readMeta("install_completed_at");
    if (idb) return idb;
  } catch (_) { /* noop */ }
  return null;
}

/** Reset install state — used when re-running setup or wiping the
 * device. Deliberately does NOT touch the actual data stores. */
export async function clearInstallState() {
  try {
    await writeMeta("install_completed_at", null);
    await writeMeta("install_snapshot_version", null);
    await writeMeta("install_snapshot_id", null);
    await writeMeta("install_image_count", null);
  } catch (_) { /* noop */ }
  // Session 25.9 — also wipe the localStorage backup flag so a retry
  // starts from truly-clean state.
  // Session 27.62 — added snapshot_version key.
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("nfcs:install:completed_at_backup");
      localStorage.removeItem("nfcs:install:snapshot_id");
      localStorage.removeItem("nfcs:install:snapshot_version");
    }
  } catch (_) { /* noop */ }
}

/**
 * Verify the device is genuinely ready for offline operation. Runs a
 * series of checks and returns a structured result the UI can render:
 *
 *   { ok: boolean, checks: [{ name, ok, detail }], failure_reason? }
 *
 * The install flow marks the installation "complete" ONLY if this
 * returns ok=true. Settings can re-run it at any time for diagnostics.
 *
 * Checks performed:
 *   • Snapshot present in IndexedDB
 *   • Business record present and matches stored slug (if any)
 *   • Core operational collections are non-empty (categories/menu_items/users/tables)
 *   • At least one user has offline credentials stored
 *   • Image cache is populated (Cache Storage API — best-effort)
 *   • IndexedDB read integrity (can we count each store without error?)
 */
export async function verifyOfflineReadiness(options = {}) {
  const checks = [];
  // Session 25.7 — checks now carry an optional `severity`. Only
  // `severity: "required"` (default) contributes to the overall `ok`
  // verdict. `severity: "info"` is informational — the diagnostics UI
  // still shows the tick/cross, but a missing "info" check does NOT
  // prevent install-complete. This aligns with the pre-auth install
  // token flow where NO user has signed in yet (and therefore has no
  // offline credentials) but the device is otherwise ready for offline
  // boot; enrollment happens naturally at first online sign-in.
  const push = (name, ok, detail, severity = "required") =>
    checks.push({ name, ok: !!ok, detail: detail || null, severity });

  // 1. Snapshot present + business record + slug
  let biz = null;
  try {
    const primed = await hasLocalSnapshot();
    push("snapshot_present", primed, primed ? null : "No snapshot has been downloaded yet.");
    biz = await getBusiness();
    push("business_record", !!biz, biz ? null : "Business record missing from local DB.");
    const storedSlug = (await readMeta("business_slug")) || null;
    const bizSlug = (biz && biz.slug) || null;
    push(
      "business_slug_match",
      !storedSlug || !bizSlug || storedSlug === bizSlug,
      storedSlug && bizSlug && storedSlug !== bizSlug
        ? `Slug mismatch (device=${storedSlug}, business=${bizSlug}).`
        : null,
    );
  } catch (e) {
    push("snapshot_present", false, String(e && e.message || e));
  }

  // 2. Core operational collections.
  //    - REQUIRED  (fail if empty): categories, menu_items — a POS with
  //      no products literally cannot take orders.
  //    - INFO      (report but pass): users, tables — legitimately empty
  //      for many businesses (pre-auth install token flow starts with 0
  //      users; takeaway / counter-service venues have 0 tables). Empty
  //      here is a normal configuration, not a broken install.
  const REQUIRED_STORES = ["categories", "menu_items"];
  const INFO_STORES = ["users", "tables"];
  const counts = {};
  for (const s of REQUIRED_STORES) {
    try {
      const rows = await readAll(s);
      counts[s] = rows.length;
      push(`store_${s}`, rows.length > 0, rows.length === 0 ? `${s} store is empty.` : `${rows.length} record(s).`);
    } catch (e) {
      push(`store_${s}`, false, String(e && e.message || e));
    }
  }
  for (const s of INFO_STORES) {
    try {
      const rows = await readAll(s);
      counts[s] = rows.length;
      push(
        `store_${s}`,
        rows.length > 0,
        rows.length === 0
          ? `${s} store is empty — this is expected if the business hasn't configured any ${s} yet.`
          : `${rows.length} record(s).`,
        "info",
      );
    } catch (e) {
      push(`store_${s}`, false, String(e && e.message || e), "info");
    }
  }

  // 3. Offline credentials — INFORMATIONAL check. Users enroll their
  //    per-user PBKDF2 verifier when they sign in online for the first
  //    time on this device. A brand-new install (especially via the
  //    pre-auth install-token flow) has zero enrolments — that's fine,
  //    the device is still perfectly ready for offline BOOT; offline
  //    SIGN-IN just requires an online login first. Marking this as
  //    "info" means the tick/cross appears in diagnostics but a missing
  //    enrolment does NOT block install-complete.
  try {
    const ids = await listOfflineCredentialUserIds();
    push(
      "offline_credentials",
      ids.length > 0,
      ids.length
        ? `${ids.length} user(s) enrolled for offline sign-in.`
        : "No user has enrolled yet. Sign in online once on this device to enable offline PIN sign-in for that user.",
      "info",
    );
  } catch (e) {
    push("offline_credentials", false, String(e && e.message || e), "info");
  }

  // 4. Image cache health (best-effort — Cache Storage API)
  try {
    if (typeof caches !== "undefined") {
      const cache = await caches.open(IMAGE_CACHE_NAME);
      const entries = await cache.keys();
      const expected = Number(await readMeta("install_image_count")) || 0;
      const strategy = (await readMeta("image_cache_strategy")) || "unknown";
      // Accept "≥ 80% cached" as pass — smart-hybrid mode intentionally
      // leaves some full-size images to fetch on-demand.
      const threshold = Math.max(1, Math.floor(expected * 0.8));
      const ok = strategy === "none" ? true : (expected === 0 || entries.length >= threshold);
      push(
        "image_cache",
        ok,
        expected === 0
          ? "No images required for offline operation."
          : `${entries.length}/${expected} images cached (strategy: ${strategy}).`,
      );
    } else {
      push("image_cache", true, "Cache Storage API unavailable — images will load on demand.");
    }
  } catch (e) {
    // Cache Storage errors are non-fatal; degrade gracefully.
    push("image_cache", true, "Cache Storage check skipped: " + String(e && e.message || e));
  }

  // 5. IDB read integrity (already implicit if we got here, but be
  // explicit so the diagnostics UI shows it as a passing check).
  push("idb_read_integrity", true, `Read ${Object.keys(counts).length} stores without error.`);

  // 6. Service Worker + app shell — the whole POINT of offline is
  // that the app can boot without the network. If the SW isn't
  // caching the shell, everything above is academic. These checks
  // run in the browser only (skipped in tests).
  try {
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      const controlled = !!navigator.serviceWorker.controller;
      // Session 27.74 — Downgraded to INFO severity. On Capacitor APKs
      // and Tauri EXEs the app shell is baked into the native binary —
      // navigator.serviceWorker.controller is always null there because
      // Chromium's WebView doesn't register the SW for `file://` and
      // `capacitor://` schemes. Keeping this as REQUIRED was the exact
      // reason APK installs got stuck at "8 of 12 checks passed" and
      // showed the "Offline setup is incomplete" error even though the
      // device was perfectly ready to run offline. PWA browsers still
      // see the tick when a SW eventually registers.
      push(
        "service_worker_active",
        controlled,
        controlled
          ? "A service worker is controlling this page."
          : "No service worker is active. Fine on native (APK/EXE) — the shell is baked into the binary. On PWA it will register within seconds.",
        "info",
      );
    } else {
      push("service_worker_active", false, "Service workers are not supported in this browser.", "info");
    }
  } catch (e) {
    push("service_worker_active", false, String(e && e.message || e), "info");
  }

  try {
    if (typeof caches !== "undefined") {
      const shell =
        (await caches.match("/index.html", { ignoreSearch: true })) ||
        (await caches.match("/", { ignoreSearch: true }));
      // Session 27.69 — Marked INFO severity. On Capacitor APKs / Tauri
      // EXEs the app shell is baked into the native binary — the service
      // worker cache doesn't apply. On PWAs the shell caches within a
      // few seconds of first load. Either way, a missing entry here is
      // NOT a blocker to declaring the device offline-ready; the shell
      // will boot offline through the native container OR through the
      // background-precached service worker. This check was previously
      // REQUIRED and was the exact reason first-launch APK installs got
      // stuck at "Offline setup is incomplete" and re-ran the whole
      // download wizard every single login.
      push(
        "app_shell_cached",
        !!shell,
        shell
          ? "App shell is cached; the PWA will boot without internet."
          : "App shell not in the service-worker cache. Fine on native (APK/EXE) or first PWA visit — the service worker will fill it in shortly.",
        "info",
      );
    } else {
      push("app_shell_cached", false, "Cache Storage API unavailable (expected on native shells).", "info");
    }
  } catch (e) {
    push("app_shell_cached", false, String(e && e.message || e));
  }

  // Overall verdict is decided by REQUIRED checks only. Informational
  // checks appear in diagnostics but never block install-complete —
  // e.g. an install-token-provisioned device with zero per-user
  // enrolments is still fully "offline ready" for the boot / dashboard-
  // render / local-data-serve paths.
  const requiredChecks = checks.filter((c) => c.severity !== "info");
  const failing = requiredChecks.filter((c) => !c.ok);
  return {
    ok: failing.length === 0,
    checks,
    failure_reason: failing.length ? failing[0].detail || failing[0].name : null,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Image caching (Cache Storage API — separate from IDB)
// ═══════════════════════════════════════════════════════════════════════════
// The install progress screen calls `cacheImageManifest(urls)` after
// downloading the snapshot. Strategy is decided by menu size:
//   • ≤ 50 URLs   → "all"     : cache everything up-front (small menus)
//   • > 50 URLs   → "thumb"   : cache first 30 (heuristic); progressive
//                               fetches will fill the rest lazily.
// The final decision + counts are persisted in meta so the readiness
// verification can grade the cache honestly.

export const IMAGE_CACHE_NAME = "nfcs-images-v1";

function _decideImageCacheStrategy(urls) {
  if (!Array.isArray(urls) || urls.length === 0) return { mode: "none", subset: [] };
  if (urls.length <= 50) return { mode: "all", subset: urls };
  return { mode: "thumb", subset: urls.slice(0, 30) };
}

/**
 * Cache the given image URLs into the Cache Storage API. Progress is
 * reported via the `onProgress({ done, total, url })` callback. Always
 * resolves — network errors on individual images are skipped (offline
 * readiness only requires 80% success).
 */
export async function cacheImageManifest(urls, { onProgress } = {}) {
  if (typeof caches === "undefined") {
    await writeMeta("image_cache_strategy", "unavailable");
    await writeMeta("install_image_count", 0);
    return { cached: 0, total: 0, strategy: "unavailable" };
  }
  const { mode, subset } = _decideImageCacheStrategy(urls || []);
  await writeMeta("image_cache_strategy", mode);
  await writeMeta("install_image_count", subset.length);
  if (subset.length === 0) return { cached: 0, total: 0, strategy: mode };
  const cache = await caches.open(IMAGE_CACHE_NAME);
  let done = 0;
  let cached = 0;
  // Sequential fetch — restaurant Wi-Fi is often thin, and 30-50 image
  // fetches are quick enough sequentially that we avoid overwhelming
  // the connection. Cache Storage API deduplicates identical URLs.
  for (const url of subset) {
    try {
      const req = new Request(url, { credentials: "omit", mode: "cors" });
      const res = await fetch(req);
      if (res.ok || res.type === "opaque") {
        await cache.put(req, res.clone());
        cached += 1;
      }
    } catch (_) { /* skip — best-effort */ }
    done += 1;
    if (typeof onProgress === "function") {
      try { onProgress({ done, total: subset.length, url }); } catch (_) { /* noop */ }
    }
  }
  return { cached, total: subset.length, strategy: mode };
}

// ═══════════════════════════════════════════════════════════════════════════
// Local Event Engine (Session 25.5 skeleton — activated in Session 26)
// ═══════════════════════════════════════════════════════════════════════════
// The Local Event Engine treats every restaurant action as an EVENT with a
// unique Change ID that flows through the lifecycle:
//
//   LOCAL → BROADCAST → PENDING → SYNCED
//
// This session only lays down the plumbing: two IndexedDB stores
// (`event_log` for a bounded ring-buffer of the last 500 events, and
// `event_queue` for pending mutations awaiting cloud sync) plus a few
// helpers. Session 26 will wire the write path into the app.
//
// `event_log` is capped at EVENT_LOG_MAX rows via a lightweight trim
// on write. That keeps IndexedDB usage bounded on tablets that run
// for months without a reinstall.

const EVENT_LOG_MAX = 500;

/**
 * Append an event to the local Event Log. Ring-buffered — when the
 * log exceeds EVENT_LOG_MAX, the oldest entries are pruned. Best-effort:
 * never throws (a failure to log must not break a write path).
 *
 * The event shape is expected to match `lib/events.js#createChangeEnvelope`
 * output. At minimum: { change_id, ts, event_type, ... }.
 */
export async function appendEventLog(event) {
  if (!event || typeof event !== "object") return;
  try {
    // Ensure the row has a stable `id` for the object store keyPath.
    const row = { id: event.change_id || event.id || _nowKey(), ...event };
    await putOne("event_log", row);
    // Prune anything beyond EVENT_LOG_MAX (oldest first). We compute the
    // trim only when the store grows past the cap so the fast path is
    // just a single put().
    const db = await openLocalDb();
    let count = 0;
    try {
      const cx = _tx(db, ["event_log"], "readonly");
      count = await _promisify(cx.objectStore("event_log").count());
    } catch (_) { count = 0; }
    if (count > EVENT_LOG_MAX) {
      try {
        const rows = await readAll("event_log");
        rows.sort((a, b) => String(a.ts || "").localeCompare(String(b.ts || "")));
        const excess = rows.slice(0, Math.max(0, count - EVENT_LOG_MAX));
        for (const r of excess) {
          try { await deleteOne("event_log", r.id); } catch (_) { /* noop */ }
        }
      } catch (_) { /* noop */ }
    }
  } catch (_) { /* silent — event logging must never break writes */ }
}

function _nowKey() {
  // Fallback id for rows without a change_id.
  return "log_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 8);
}

/**
 * Read the most-recent `limit` events from the local log, newest first.
 * Used by Settings' Local Status card for the "recent events" pane and
 * by support tooling during offline diagnostics.
 */
export async function readRecentEventLog(limit = 100) {
  try {
    const rows = await readAll("event_log");
    rows.sort((a, b) => String(b.ts || "").localeCompare(String(a.ts || "")));
    return rows.slice(0, Math.max(0, Number(limit) || 0));
  } catch (_) { return []; }
}

/** How many events are currently in the event log? */
export async function getEventLogCount() {
  try {
    const db = await openLocalDb();
    const tx = _tx(db, ["event_log"], "readonly");
    return await _promisify(tx.objectStore("event_log").count());
  } catch (_) { return 0; }
}

/** How many events are pending cloud sync? (Session 26 will populate.) */
export async function getEventQueueCount() {
  try {
    const db = await openLocalDb();
    const tx = _tx(db, ["event_queue"], "readonly");
    return await _promisify(tx.objectStore("event_queue").count());
  } catch (_) { return 0; }
}

/** Clear the entire event log (support action — never called automatically). */
export async function clearEventLog() {
  try {
    const db = await openLocalDb();
    const tx = _tx(db, ["event_log"], "readwrite");
    tx.objectStore("event_log").clear();
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
    });
  } catch (_) { /* noop */ }
}

